import { ApiResponse, GlobalStore, UserStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { OneToolService } from '../../services/oneToolService';
import { BrokerFormStore } from './brokerFormStore';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { BrokerService } from '@roc/feature-brokers';

export class BrokerStore {
  private globalStore: GlobalStore;
  private userStore: UserStore;
  private oneToolService: OneToolService;
  public brokerFormStore: BrokerFormStore;
  brokerService: BrokerService;
  public brokerVendorClosingSettings;
  public lenderBrokerData: any[];

  constructor(globalStore, userStore: UserStore) {
    this.brokerService = new BrokerService();
    this.globalStore = globalStore;
    this.userStore = userStore;
    this.oneToolService = new OneToolService();
    this.brokerFormStore = new BrokerFormStore(globalStore);
    this.lenderBrokerData = [];
    this.brokerService = new BrokerService();

    makeObservable(this, {
      reset: action,
      getLenderBrokerDropdownData: flow,
      lenderBrokerData: observable,
      getBrokerById: flow,
      validateBroker_quote: action,
      validateBroker_preflight: action,
      loadBroker: action,
      loadBrokerData: action,
      getBroker: action,
      getBrokerVendorClosingSettingsByBrokerId: flow,
      brokerVendorClosingSettings: observable,
      getCurrentBroker: flow,
      handleBrokerFeeChange: action,
      handleDisplayToggle: action,
      syncBrokerFeeValues: action,
      updateBrokerFields: action,
      resetBrokerFees: action,
    });
  }

  *getLenderBrokerDropdownData() {
    try {
      const response = yield this.oneToolService.getLenderBrokerData();
      this.lenderBrokerData = response.data.data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *getBrokerById(brokerId: number) {
    try {
      const response = yield this.oneToolService.getBrokerById(brokerId);
      this.loadBrokerFromSelection(response.data.data, this.brokerFormStore.getFormValues());
      this.brokerFormStore.onFieldChange('hasBroker', true);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  loadBrokerFromSelection(broker, brokerFormValues) {
    this.loadBroker({
      ...broker ?? {},
      brokerType: brokerFormValues.brokerType || 'ON_THE_HUD',
      brokerPoints: brokerFormValues?.brokerPoints ?? '',
    })
  }

  loadBrokerData(quoteData) {
    this.loadBroker(quoteData.broker);
    this.brokerFormStore.onFieldChange("displayBrokerFee", quoteData.uiState?.displayBrokerFee)
  }

  loadBroker(broker) {
    this.brokerFormStore.loadForm({
      brokerId: broker?.brokerId ?? '',
      brokerType: broker?.brokerType ?? '',
      brokerPoints: broker?.brokerPoints ?? '',
      companyName: broker?.companyName ?? '',
      companyEin: broker?.companyEin ?? '',
      contactFirstName: broker?.contactFirstName ?? '',
      contactLastName: broker?.contactLastName ?? '',
      contactEmail: broker?.contactEmail ?? '',
      contactPhoneNumber: broker?.contactPhoneNumber ?? '',
      hasBroker: broker?.hasBroker ?? null,
    });
  }

  reset() {
    this.brokerFormStore.reset();
  }

  *getCurrentBroker() {
    if (this.globalStore?.userFeatures?.isBroker) {
      const response = yield this.brokerService.getBrokerByUserId(this.userStore?.userInformation?.userId);
      const broker = response.data.data;
      this.loadBroker({
        ...broker,
        brokerId: broker?.lenderId,
        hasBroker: true,
        brokerType: 'ON_THE_HUD',
        brokerPoints: 0,
        companyName: broker?.company?.name,
        companyEin: broker?.company?.einNumber,
        contactFirstName: this.userStore.userInformation?.firstName,
        contactLastName: this.userStore.userInformation?.lastName,
        contactEmail: this.userStore.userInformation?.emailAddress,
        contactPhoneNumber: this.userStore.userInformation?.cellPhone,
      });
    }
  }

  getBroker() {
    const values = this.brokerFormStore.getFormValues();
    const brokerType = values.brokerType
      || (!this.globalStore.lenderInfo?.isInternal ? 'ON_THE_HUD' : null);
    return {
      ...values,
      brokerType,
    };
  }

  setHasBroker(value) {
    this.brokerFormStore.reset();
    this.brokerFormStore.onFieldChange('hasBroker', value);
  }

  validateBroker_quote() {
    this.brokerFormStore.onFieldChange('isPreflight', Boolean(false));
    this.brokerFormStore.onFieldChange('isQuote', Boolean(true));
    this.brokerFormStore.runFormValidationWithMessage();
    return this.brokerFormStore.form.meta.isValid;
  }

  validateBroker_preflight() {
    this.brokerFormStore.onFieldChange('isPreflight', Boolean(true));
    this.brokerFormStore.onFieldChange('isQuote', Boolean(false));
    if (this.getBroker().hasBroker) {
      this.brokerFormStore.runFormValidationWithMessage();
      return this.brokerFormStore.form.meta.isValid;
    } else {
      return true;
    }
  }

  *getBrokerVendorClosingSettingsByBrokerId(brokerId) {
    try {
      const lenderId = this.globalStore.lenderInfo?.lenderId;
      const response: ApiResponse = yield this.brokerService.getBrokerVendorClosingSettingsByBrokerId(brokerId, lenderId);
      this.brokerVendorClosingSettings = response.data.data;
      return response.data.data;
    }
    catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting attorneys for brokers.',
      });
    }
  }

  updateBrokerFields(amount: number, totalLoanAmount: number) {
    const loan = parseFloat(totalLoanAmount as any) || 0;
    const numericAmount = parseFloat(amount as any) || 0;

    const calculatedPercentage = loan > 0 ? (numericAmount / loan) * 100 : 0;

    this.brokerFormStore.onFieldChange("brokerAmount", +numericAmount.toFixed(2));
    this.brokerFormStore.onFieldChange("brokerPoints", +calculatedPercentage.toFixed(2));
  }

  resetBrokerFees() {
    this.brokerFormStore.onFieldChange("brokerAmount", 0);
    this.brokerFormStore.onFieldChange("brokerPoints", 0);
  }

  syncBrokerFeeValues(totalLoanAmount: number, brokerPoints: number, brokerAmount: number, displayBrokerFee: boolean) {
    const loan = parseFloat(totalLoanAmount as any) || 0;
    const points = parseFloat(brokerPoints as any) || 0;
    const amount = parseFloat(brokerAmount as any) || 0;

    if (!loan || loan <= 0) {
      this.resetBrokerFees();
      return;
    }

    if (displayBrokerFee) {
      this.updateBrokerFields(amount, loan);
    } else {
      const calculatedAmount = (points / 100) * loan;
      this.updateBrokerFields(calculatedAmount, loan);
    }
  }

  handleBrokerFeeChange(value: number, totalLoanAmount: number) {
    const form = this.brokerFormStore.getFormValues() || {};
    const loan = parseFloat(totalLoanAmount as any) || 0;

    if (!loan || value < 0) {
      this.resetBrokerFees();
      return;
    }

    if (form.displayBrokerFee) {
      this.syncBrokerFeeValues(loan, form.brokerPoints, value, true);
    } else {
      this.syncBrokerFeeValues(loan, value, form.brokerAmount, false);
    }
  }

  handleDisplayToggle(totalLoanAmount: number) {
    const form = this.brokerFormStore.getFormValues() || {};
    const newDisplayFee = !form.displayBrokerFee;

    this.brokerFormStore.onFieldChange("displayBrokerFee", newDisplayFee);

    this.syncBrokerFeeValues(
      totalLoanAmount,
      form.brokerPoints,
      form.brokerAmount,
      newDisplayFee
    );
  }

  initializeBrokerFee(totalLoanAmount: number) {
    const form = this.brokerFormStore.getFormValues() || {};
    this.syncBrokerFeeValues(
      totalLoanAmount,
      form.brokerPoints,
      form.brokerAmount,
      form.displayBrokerFee
    );
  }
}
